import ConfirmDialog from "components/common/ConfirmDialog";
import {
  AskStrikeUpButton,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  InfoBox,
  Input,
  RefreshButton,
  Text,
  Textarea,
  VStack,
} from "components/ui";
import { notify } from "components/ui/toaster";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import { MdAutoAwesome, MdClear, MdSave, MdSend } from "react-icons/md";
import { authService } from "services/authService";
import { companyService } from "services/companyService";
import {
  GuardrailsPolicy,
  GuardrailsTestResult,
  guardrailsService,
} from "services/guardrailsService";
import { serverService } from "services/serverService";
import PolicyHistoryCard from "./PolicyHistoryCard";

interface PolicyError {
  reason: string;
  violations: string[];
}

const Guardrails = () => {
  const { t } = useTranslation();
  const [policy, setPolicy] = useState<GuardrailsPolicy | null>(null);
  const [companyPolicyText, setCompanyPolicyText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | PolicyError>("");
  const [success, setSuccess] = useState("");
  const [testMessage, setTestMessage] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<GuardrailsTestResult | null>(
    null,
  );
  const [violationResponseText, setViolationResponseText] = useState("");
  const [savingViolationResponse, setSavingViolationResponse] = useState(false);
  const [website, setWebsite] = useState("");
  const [savingWebsite, setSavingWebsite] = useState(false);
  const [hasServers, setHasServers] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateConfirmOpen, setRegenerateConfirmOpen] = useState(false);
  // Bumped after anything that can add a history row, so the history card re-reads.
  const [historyRefreshToken, setHistoryRefreshToken] = useState(0);
  const refreshHistory = () => setHistoryRefreshToken((n) => n + 1);

  const loadPolicy = async () => {
    setLoading(true);
    try {
      const data = await guardrailsService.getPolicy();
      setPolicy(data);
      setCompanyPolicyText(data.companyPolicy || "");
      setViolationResponseText(data.violationResponseText || "");
    } catch (err: any) {
      setError(
        err.message || t("app.security.guardrailsPolicy.errors.loadFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPolicy();
    // Seed website from cached company; refresh in background to catch admin edits made elsewhere.
    const cached = companyService.getCurrentCompany();
    setWebsite(cached?.website ?? "");
    authService
      .fetchAndStoreCurrentUser()
      .then(() => {
        const fresh = companyService.getCurrentCompany();
        setWebsite(fresh?.website ?? "");
      })
      .catch(() => {});
    serverService
      .getAllServers()
      .then((servers) =>
        setHasServers(servers.some((s) => s.isActive === true)),
      )
      .catch(() => setHasServers(false));
  }, [t]);

  const handleSaveWebsite = async () => {
    const company = companyService.getCurrentCompany();
    if (!company?.id) return;
    setSavingWebsite(true);
    try {
      await companyService.updateCompany(company.id, {
        website: website.trim() || undefined,
      });
      await authService.fetchAndStoreCurrentUser();
      notify({
        type: "success",
        title: t("app.guardrails.websiteSaved"),
      });
    } catch (err: any) {
      notify({
        type: "error",
        title: t("app.guardrails.websiteSaveFailed"),
        description: err.response?.data?.message || err.message || undefined,
      });
    } finally {
      setSavingWebsite(false);
    }
  };

  const openRegenerateConfirm = () => {
    if (!website.trim() || !hasServers) return;
    setRegenerateConfirmOpen(true);
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const fresh = await guardrailsService.regenerate();
      setPolicy(fresh);
      setCompanyPolicyText(fresh.companyPolicy || "");
      refreshHistory();
      notify({
        type: "success",
        title: t("app.guardrails.regenerateSuccess"),
      });
      setRegenerateConfirmOpen(false);
    } catch (err: any) {
      const data = err.response?.data;
      if (data?.violations && Array.isArray(data.violations)) {
        notify({
          type: "error",
          title: data.reason ?? t("app.guardrails.regenerateFailed"),
          description: data.violations.join(" • "),
        });
      } else {
        notify({
          type: "error",
          title: t("app.guardrails.regenerateFailed"),
          description: data?.error || err.message || undefined,
        });
      }
      setRegenerateConfirmOpen(false);
    } finally {
      setRegenerating(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await guardrailsService.updateCompanyPolicy({ companyPolicyText });
      const successKey = companyPolicyText.trim()
        ? "app.security.guardrailsPolicy.savePolicySuccess"
        : "app.security.guardrailsPolicy.clearPolicySuccess";
      setSuccess(t(successKey));
      setTimeout(() => setSuccess(""), 3000);
      await loadPolicy();
      refreshHistory();
    } catch (err: any) {
      const data = err.response?.data;
      if (data?.violations && Array.isArray(data.violations)) {
        setError({
          reason:
            data.reason ?? t("app.security.guardrailsPolicy.savePolicyFailed"),
          violations: data.violations,
        });
      } else {
        setError(
          data?.error ||
            err.message ||
            t("app.security.guardrailsPolicy.savePolicyFailed"),
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    setCompanyPolicyText("");
  };

  const handleLoadVersion = (policyText: string) => {
    setCompanyPolicyText(policyText);
    notify({
      type: "info",
      title: t("app.security.guardrailsPolicy.history.loadedIntoEditor"),
    });
  };

  const handleRefresh = () => {
    loadPolicy();
    refreshHistory();
  };

  const handleTest = async () => {
    if (!testMessage.trim()) return;
    setTesting(true);
    setTestResult(null);
    try {
      const result = await guardrailsService.testMessage(testMessage.trim());
      setTestResult(result);
    } catch {
      setTestResult({
        allowed: false,
        violations: [],
        reason: t("app.security.guardrailsPolicy.test.error"),
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSaveViolationResponse = async () => {
    setSavingViolationResponse(true);
    setError("");
    setSuccess("");
    try {
      await guardrailsService.updateViolationResponse(
        violationResponseText.trim() || null,
      );
      setSuccess(
        t("app.security.guardrailsPolicy.violationResponse.saveSuccess"),
      );
      setTimeout(() => setSuccess(""), 3000);
    } catch {
      setError(t("app.security.guardrailsPolicy.violationResponse.saveFailed"));
    } finally {
      setSavingViolationResponse(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <Box mb={5}>
          <Heading size="lg" mb={2}>
            {t("app.security.title")} - {t("app.security.guardrails.title")}
          </Heading>
        </Box>
        <Card>
          <Card.Body>
            <Text>{t("app.security.guardrailsPolicy.loading")}</Text>
          </Card.Body>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Flex
        justify="space-between"
        align="flex-start"
        mb={5}
        wrap="wrap"
        gap={3}
      >
        <Box>
          <Heading size="lg" mb={2}>
            {t("app.security.title")} - {t("app.security.guardrails.title")}
          </Heading>
          <Text color={{ base: "gray.600", _dark: "gray.400" }}>
            {t("app.security.guardrailsPolicy.systemPolicyDescription")}
          </Text>
        </Box>
        <HStack gap={3}>
          <AskStrikeUpButton
            context={t("app.askStrikeUp.context.guardrails")}
          />
          <RefreshButton onClick={handleRefresh} loading={loading} />
        </HStack>
      </Flex>

      <VStack align="stretch" gap={6}>
        {error && (
          <InfoBox variant="error">
            <HStack justify="space-between" align="start">
              <Box flex={1}>
                {typeof error === "string" ? (
                  <Text color="red.700">{error}</Text>
                ) : (
                  <VStack align="stretch" gap={2}>
                    <Text color="red.700" fontWeight="semibold">
                      {error.reason}
                    </Text>
                    {error.violations.length > 0 && (
                      <Box pl={3}>
                        {error.violations.map((v, i) => (
                          <Text key={i} fontSize="sm" color="red.700">
                            • {v}
                          </Text>
                        ))}
                      </Box>
                    )}
                  </VStack>
                )}
              </Box>
              <Button onClick={() => setError("")} size="sm" variant="ghost">
                {t("app.common.dismiss")}
              </Button>
            </HStack>
          </InfoBox>
        )}

        {success && (
          <InfoBox variant="success">
            <HStack justify="space-between">
              <Text color="green.700">{success}</Text>
              <Button onClick={() => setSuccess("")} size="sm" variant="ghost">
                {t("app.common.dismiss")}
              </Button>
            </HStack>
          </InfoBox>
        )}

        {/* Company Policy (Editable) */}
        <Card>
          <Card.Body>
            <VStack align="stretch" gap={3}>
              <Box>
                <Heading size="sm" mb={1}>
                  {t("app.security.guardrailsPolicy.companyPolicy")}
                </Heading>
                <Text
                  fontSize="xs"
                  color={{ base: "gray.600", _dark: "gray.400" }}
                >
                  {t("app.security.guardrailsPolicy.companyPolicyDescription")}
                </Text>
              </Box>
              <Textarea
                value={companyPolicyText}
                onChange={(e) => setCompanyPolicyText(e.target.value)}
                placeholder={t(
                  "app.security.guardrailsPolicy.companyPolicyPlaceholder",
                )}
                rows={12}
                fontFamily="monospace"
                fontSize="sm"
              />
              <HStack justify="flex-end">
                {companyPolicyText.trim() && (
                  <Button
                    variant="ghost"
                    onClick={handleClear}
                    disabled={saving || loading}
                  >
                    <MdClear style={{ marginRight: "0.5rem" }} />
                    {t("app.security.guardrailsPolicy.clearPolicy")}
                  </Button>
                )}
                <Button
                  colorPalette="blue"
                  onClick={handleSave}
                  loading={saving}
                  disabled={loading}
                >
                  <MdSave style={{ marginRight: "0.5rem" }} />
                  {t("app.security.guardrailsPolicy.savePolicy")}
                </Button>
              </HStack>
            </VStack>
          </Card.Body>
        </Card>

        {/* Test Guardrails */}
        <Card>
          <Card.Body>
            <VStack align="stretch" gap={3}>
              <Box>
                <Heading size="sm" mb={1}>
                  {t("app.security.guardrailsPolicy.test.title")}
                </Heading>
                <Text
                  fontSize="xs"
                  color={{ base: "gray.600", _dark: "gray.400" }}
                >
                  {t("app.security.guardrailsPolicy.test.description")}
                </Text>
              </Box>
              <Textarea
                value={testMessage}
                onChange={(e) => {
                  setTestMessage(e.target.value);
                  setTestResult(null);
                }}
                placeholder={t(
                  "app.security.guardrailsPolicy.test.placeholder",
                )}
                rows={4}
                fontSize="sm"
              />
              <HStack justify="flex-end">
                <Button
                  colorPalette="purple"
                  onClick={handleTest}
                  loading={testing}
                  disabled={!testMessage.trim()}
                >
                  <MdSend style={{ marginRight: "0.5rem" }} />
                  {t("app.security.guardrailsPolicy.test.send")}
                </Button>
              </HStack>
              {testResult && (
                <Box
                  p={4}
                  borderRadius="md"
                  border="1px solid"
                  borderColor={
                    testResult.allowed
                      ? { base: "green.300", _dark: "green.600" }
                      : { base: "red.300", _dark: "red.600" }
                  }
                  bg={
                    testResult.allowed
                      ? { base: "green.50", _dark: "green.950" }
                      : { base: "red.50", _dark: "red.950" }
                  }
                >
                  <VStack align="stretch" gap={2}>
                    <Text
                      fontWeight="bold"
                      color={
                        testResult.allowed
                          ? { base: "green.700", _dark: "green.300" }
                          : { base: "red.700", _dark: "red.300" }
                      }
                    >
                      {testResult.allowed
                        ? t("app.security.guardrailsPolicy.test.resultAllowed")
                        : t("app.security.guardrailsPolicy.test.resultBlocked")}
                    </Text>
                    <Text fontSize="sm">{testResult.reason}</Text>
                    {testResult.violations.length > 0 && (
                      <Box>
                        <Text fontSize="xs" fontWeight="semibold" mb={1}>
                          {t("app.security.guardrailsPolicy.test.violations")}
                        </Text>
                        <VStack align="stretch" gap={1}>
                          {testResult.violations.map((v, i) => (
                            <Text key={i} fontSize="xs">
                              • {v}
                            </Text>
                          ))}
                        </VStack>
                      </Box>
                    )}
                  </VStack>
                </Box>
              )}
            </VStack>
          </Card.Body>
        </Card>

        {/* Violation Response Message */}
        <Card>
          <Card.Body>
            <VStack align="stretch" gap={3}>
              <Box>
                <Heading size="sm" mb={1}>
                  {t("app.security.guardrailsPolicy.violationResponse.title")}
                </Heading>
                <Text
                  fontSize="xs"
                  color={{ base: "gray.600", _dark: "gray.400" }}
                >
                  {t(
                    "app.security.guardrailsPolicy.violationResponse.description",
                  )}
                </Text>
              </Box>
              <Textarea
                value={violationResponseText}
                onChange={(e) => setViolationResponseText(e.target.value)}
                placeholder={t(
                  "app.security.guardrailsPolicy.violationResponse.placeholder",
                )}
                rows={3}
                fontSize="sm"
              />
              <HStack justify="flex-end">
                <Button
                  colorPalette="blue"
                  onClick={handleSaveViolationResponse}
                  loading={savingViolationResponse}
                  disabled={loading}
                >
                  <MdSave style={{ marginRight: "0.5rem" }} />
                  {t("app.security.guardrailsPolicy.violationResponse.save")}
                </Button>
              </HStack>
            </VStack>
          </Card.Body>
        </Card>

        {/* Domain-aware Guardrails — website + AI regenerate (bottom of page) */}
        <Card>
          <Card.Body>
            <VStack align="stretch" gap={3}>
              <Box>
                <Heading size="sm" mb={1}>
                  {t("app.guardrails.websiteCardTitle")}
                </Heading>
                <Text
                  fontSize="xs"
                  color={{ base: "gray.600", _dark: "gray.400" }}
                >
                  {t("app.guardrails.websiteCardDescription")}
                </Text>
              </Box>
              <HStack gap={2} align="flex-end">
                <Box flex={1}>
                  <Text fontSize="xs" mb={1} fontWeight="semibold">
                    {t("app.guardrails.website")}
                  </Text>
                  <Input
                    type="url"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder={t("app.guardrails.websitePlaceholder")}
                    size="md"
                  />
                </Box>
                <Button
                  onClick={handleSaveWebsite}
                  loading={savingWebsite}
                  disabled={savingWebsite}
                  variant="outline"
                >
                  <MdSave style={{ marginRight: "0.5rem" }} />
                  {t("app.security.guardrailsPolicy.savePolicy")}
                </Button>
              </HStack>
              <HStack justify="space-between" align="center">
                <Text
                  fontSize="xs"
                  color={{ base: "gray.600", _dark: "gray.400" }}
                >
                  {!website.trim()
                    ? t("app.guardrails.regenerateNoWebsite")
                    : !hasServers
                      ? t("app.guardrails.regenerateNoServers")
                      : t("app.guardrails.regenerateHint")}
                </Text>
                <Button
                  colorPalette="purple"
                  onClick={openRegenerateConfirm}
                  loading={regenerating}
                  disabled={!website.trim() || !hasServers || regenerating}
                >
                  <MdAutoAwesome style={{ marginRight: "0.5rem" }} />
                  {t("app.guardrails.regenerate")}
                </Button>
              </HStack>
            </VStack>
          </Card.Body>
        </Card>

        {/* Change history — every saved policy version (bottom of page) */}
        <PolicyHistoryCard
          refreshToken={historyRefreshToken}
          onLoadVersion={handleLoadVersion}
        />
      </VStack>

      <ConfirmDialog
        isOpen={regenerateConfirmOpen}
        onClose={() => setRegenerateConfirmOpen(false)}
        onConfirm={handleRegenerate}
        title={t("app.guardrails.regenerateConfirmTitle")}
        message={t("app.guardrails.regenerateConfirmBody")}
        confirmLabel={t("app.guardrails.regenerate")}
        colorPalette="purple"
        loading={regenerating}
      />
    </Box>
  );
};

export default Guardrails;
